import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { Box, Button, Collapse, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import useSextforceLiveStreamAutoAnnouncementsMessages, {
    OnlyFansSextfoceLiveStreamAnnoncementMessageGroupWithMessages,
    OnlyFansSextfoceLiveStreamAnnouncementMessage,
} from '../../../../../hooks/useSextforceLiveStreamAutoAnnouncementsMessages';
import { OnlyFansSubscriber } from '../../../../../hooks/useSubscriber';
import SextforceLiveStreamAutoAnnouncementsMessageForm from './SextforceLiveStreamAutoAnnouncementsMessageForm';
import SextforceLiveStreamAutoAnnouncementsMessageGroupForm from './SextforceLiveStreamAutoAnnouncementsMessageGroupForm';
import SextforceLiveStreamAutoAnnouncementsMessageRow from './SextforceLiveStreamAutoAnnouncementsMessageRow';

const SextforceLiveStreamAutoAnnouncementsGroupRow = ({
    subscriber,
    group,
    onMessageDragStart,
    onMessageDragEnd,
    onMessageDragOver,
    onMessageDrop,
    messageDragState,
    currentDragType,
    currentDragGroupId,
}: {
    subscriber: OnlyFansSubscriber;
    group: OnlyFansSextfoceLiveStreamAnnoncementMessageGroupWithMessages;
    onMessageDragStart: (e: React.DragEvent, index: number, type: 'message', groupId: string) => void;
    onMessageDragEnd: (e: React.DragEvent) => void;
    onMessageDragOver: (e: React.DragEvent, index: number, type: 'message', groupId: string) => void;
    onMessageDrop: (e: React.DragEvent, index: number, type: 'message', groupId: string) => void;
    messageDragState: { draggedIndex: number | null; draggedOverIndex: number | null; groupId: string | null };
    currentDragType: 'group' | 'message' | null;
    currentDragGroupId: string | null;
}) => {
    const theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const {
        refetch: refetchLiveStreamAutoAnnouncementsMessages,
        sextforceLiveStreamAutoAnnouncementMessageGroupUpdate,
        isSextforceAutoAnnouncementMessageGroupUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupDelete,
        isSextforceAutoAnnouncementMessageGroupDeleteLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupActiveUpdate,
        isSextforceAutoAnnouncementMessageGroupActiveUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageCreate,
        isSextforceAutoAnnouncementMessageCreateLoading,
    } = useSextforceLiveStreamAutoAnnouncementsMessages();

    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showCreateMessageForm, setShowCreateMessageForm] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(true); // New state for collapse

    const handleGroupDelete = useCallback(
        (groupId: string) => {
            dialog
                .confirm({
                    message: 'Are you sure you want to delete this group and all its messages?',
                    title: 'Delete Live Stream Auto Announcement Group',
                    ok: { text: 'Delete', color: 'error' },
                    cancel: { text: 'Cancel', color: 'primary' },
                })
                .then(() => {
                    sextforceLiveStreamAutoAnnouncementMessageGroupDelete(groupId)
                        .then(() => {
                            enqueueSnackbar('Live Stream Auto Announcement Group deleted successfully', {
                                variant: 'success',
                                autoHideDuration: 3000,
                            });
                            refetchLiveStreamAutoAnnouncementsMessages();
                            setShowEditDialog(false);
                        })
                        .catch(error => {
                            enqueueSnackbar('Failed to delete Live Stream Auto Announcement Group', {
                                variant: 'error',
                                autoHideDuration: 3000,
                            });
                        });
                })
                .catch(() => {
                    // User cancelled the delete action
                });
        },
        [dialog, sextforceLiveStreamAutoAnnouncementMessageGroupDelete, enqueueSnackbar, refetchLiveStreamAutoAnnouncementsMessages],
    );

    const handleGroupActiveToggle = useCallback(() => {
        sextforceLiveStreamAutoAnnouncementMessageGroupActiveUpdate(group._id!, !group.active)
            .then(() => {
                enqueueSnackbar(`Live Stream Auto Announcement Group ${group.active ? 'deactivated' : 'activated'} successfully`, {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
                refetchLiveStreamAutoAnnouncementsMessages();
            })
            .catch(error => {
                enqueueSnackbar('Failed to update Live Stream Auto Announcement Group status', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });
    }, [
        group._id,
        group.active,
        sextforceLiveStreamAutoAnnouncementMessageGroupActiveUpdate,
        enqueueSnackbar,
        refetchLiveStreamAutoAnnouncementsMessages,
    ]);

    const handleCreateMessage = useCallback(
        (message: OnlyFansSextfoceLiveStreamAnnouncementMessage) => {
            const messageWithGroup = { ...message, groupId: group._id! };
            sextforceLiveStreamAutoAnnouncementMessageCreate(messageWithGroup)
                .then(newMessage => {
                    if (newMessage && newMessage._id) {
                        enqueueSnackbar('Live Stream Auto Announcement Message created successfully', {
                            variant: 'success',
                            autoHideDuration: 3000,
                        });
                        setShowCreateMessageForm(false);
                        refetchLiveStreamAutoAnnouncementsMessages();
                    } else {
                        enqueueSnackbar('Failed to create Live Stream Auto Announcement Message', {
                            variant: 'error',
                            autoHideDuration: 3000,
                        });
                    }
                })
                .catch(error => {
                    enqueueSnackbar('Failed to create Live Stream Auto Announcement Message', {
                        variant: 'error',
                        autoHideDuration: 3000,
                    });
                });
        },
        [group._id, sextforceLiveStreamAutoAnnouncementMessageCreate, enqueueSnackbar, refetchLiveStreamAutoAnnouncementsMessages],
    );

    // Toggle collapse state
    const handleToggleExpand = useCallback(() => {
        setIsExpanded(prev => !prev);
    }, []);

    // Memoize message rendering to prevent unnecessary re-renders
    const messageRows = useMemo(() => {
        if (!group.messages || group.messages.length === 0) {
            return null;
        }

        return group.messages.map((message, messageIndex) => (
            <SextforceLiveStreamAutoAnnouncementsMessageRow
                key={message._id}
                subscriber={subscriber}
                message={message}
                groupId={group._id!}
                index={messageIndex}
                onDragStart={onMessageDragStart}
                onDragEnd={onMessageDragEnd}
                onDragOver={onMessageDragOver}
                onDrop={onMessageDrop}
                isDragging={messageDragState.draggedIndex === messageIndex && messageDragState.groupId === group._id}
                draggedOverIndex={messageDragState.groupId === group._id ? messageDragState.draggedOverIndex : null}
                currentDragType={currentDragType}
                currentDragGroupId={currentDragGroupId}
                isGroupActive={group.active}
            />
        ));
    }, [
        group.messages,
        group._id,
        group.active,
        subscriber,
        onMessageDragStart,
        onMessageDragEnd,
        onMessageDragOver,
        onMessageDrop,
        messageDragState,
        currentDragType,
        currentDragGroupId,
    ]);

    return (
        <Box sx={{ p: 0, m: 0 }}>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        },
                        p: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                    onClick={handleToggleExpand}
                >
                    {/* Replaced drag handle with expand/collapse icon */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            transition: 'transform 0.2s ease',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    >
                        <ExpandLessIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {group.name}
                        </Typography>
                        {/* Display interval information */}
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CampaignIcon fontSize="small" color={group.active ? 'success' : 'disabled'} />
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                {group.announcementEveryAmount} {group.announcementEveryUnit}
                            </Typography>
                        </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <IconButton
                            size="small"
                            onClick={handleGroupActiveToggle}
                            disabled={isSextforceAutoAnnouncementMessageGroupActiveUpdateLoading}
                        >
                            <PowerSettingsNewIcon fontSize="small" color={group.active ? 'success' : 'disabled'} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                setShowEditDialog(true);
                            }}
                            disabled={isSextforceAutoAnnouncementMessageGroupUpdateLoading}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                handleGroupDelete(group._id!);
                            }}
                            disabled={isSextforceAutoAnnouncementMessageGroupDeleteLoading}
                        >
                            <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                    </Box>
                </Box>

                {showEditDialog && (
                    <SextforceLiveStreamAutoAnnouncementsMessageGroupForm
                        subscriber={subscriber as OnlyFansSubscriber}
                        initialValue={group}
                        onSubmit={updatedGroup => {
                            sextforceLiveStreamAutoAnnouncementMessageGroupUpdate(group._id!, updatedGroup)
                                .then(updatedGroup => {
                                    if (updatedGroup && updatedGroup._id) {
                                        enqueueSnackbar('Live Stream Auto Announcement Group updated successfully', {
                                            variant: 'success',
                                            autoHideDuration: 3000,
                                        });
                                        refetchLiveStreamAutoAnnouncementsMessages();
                                        setShowEditDialog(false);
                                    } else {
                                        enqueueSnackbar('Failed to update Live Stream Auto Announcement Group', {
                                            variant: 'error',
                                            autoHideDuration: 3000,
                                        });
                                    }
                                })
                                .catch(error => {
                                    console.error(error);
                                    enqueueSnackbar('Failed to update Live Stream Auto Announcement Group', {
                                        variant: 'error',
                                        autoHideDuration: 3000,
                                    });
                                });
                        }}
                        isLoading={isSextforceAutoAnnouncementMessageGroupUpdateLoading}
                        onCancel={() => {
                            setShowEditDialog(false);
                        }}
                        open={showEditDialog}
                    />
                )}

                {/* Collapsible content */}
                <Collapse in={isExpanded} timeout={300} unmountOnExit={true}>
                    <Stack direction="column" spacing={0}>
                        {messageRows || (
                            <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.text.secondary, py: 2 }}>
                                No messages in this group yet.
                            </Typography>
                        )}

                        <Box sx={{ pt: 2, pb: 2, textAlign: 'center' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setShowCreateMessageForm(true)}
                                disabled={isSextforceAutoAnnouncementMessageCreateLoading}
                            >
                                Add Message to Group
                            </Button>
                        </Box>

                        {showCreateMessageForm && (
                            <SextforceLiveStreamAutoAnnouncementsMessageForm
                                subscriber={subscriber}
                                groupId={group._id!}
                                onSubmit={handleCreateMessage}
                                isLoading={isSextforceAutoAnnouncementMessageCreateLoading}
                                onCancel={() => setShowCreateMessageForm(false)}
                                open={showCreateMessageForm}
                            />
                        )}
                    </Stack>
                </Collapse>
            </Box>
        </Box>
    );
};

export default SextforceLiveStreamAutoAnnouncementsGroupRow;
